import type {
	WebPage,
	WithContext,
	Product,
	BreadcrumbList,
	ListItem
} from 'schema-dts';
import type { Property } from '~/core/entities/property';
import { joinURL } from 'ufo';

import { PropertyPresenter } from '~/presenters/PropertyPresenter';
import { PropertyJsonLdPresenter } from '~/presenters/PropertyJsonLdPresenter';

export function usePropertyPageJsonLd(propertyGetter: () => Property | null) {
	const websiteUrl = useRuntimeConfig().public.ORIGIN;

	const property = computed<Property | null>(propertyGetter);

	const productJsonLd = computed<WithContext<Product> | null>(() => {
		if (!property.value) return null;

		return PropertyJsonLdPresenter.getProductJsonLd(property.value, {
			websiteUrl
		});
	});

	const webpageJsonLd = computed<WithContext<WebPage> | null>(() => {
		if (!property.value) return null;

		const firstPropertyVideoUrl = property.value.videos[0];

		const propertyReferenceObject = { '@id': property.value.commercialId };

		return {
			'@type': 'WebPage',
			'@context': 'https://schema.org',
			identifier: property.value.commercialId,
			name: property.value.ad.title,
			description: property.value.ad.description,
			url: PropertyPresenter.getPropertyPageUrl(property.value, websiteUrl),
			thumbnailUrl: property.value.thumbnailUrl || undefined,
			image: PropertyJsonLdPresenter.getPropertyJsonLdImageObject(
				property.value
			),
			video: firstPropertyVideoUrl
				? {
						'@type': 'VideoObject',
						embedUrl: firstPropertyVideoUrl
					}
				: undefined,
			breadcrumb: {
				'@id': `${property.value.commercialId}:breadcrumb`
			},
			about: propertyReferenceObject,
			mainEntity: propertyReferenceObject,
			offers: propertyReferenceObject
		} satisfies WithContext<WebPage>;
	});

	const propertyBreadcrumbListJsonLd =
		computed<WithContext<BreadcrumbList> | null>(() => {
			if (!property.value) return null;

			const breadcrumbItems = PropertyPresenter.getBreadcrumItems(
				property.value
			).map<ListItem>((breadcrumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				name: breadcrumb.structuredDataLabel || breadcrumb.label,
				item: joinURL(websiteUrl, breadcrumb.route)
			}));

			return {
				'@type': 'BreadcrumbList',
				'@context': 'https://schema.org',
				'@id': `${property.value.commercialId}:breadcrumb`,
				itemListElement: breadcrumbItems
			};
		});

	useJsonld(() => webpageJsonLd.value);
	useJsonld(() => productJsonLd.value);
	useJsonld(() => propertyBreadcrumbListJsonLd.value);
}
